import { useState, useEffect, useMemo } from 'react';
import {
    User, Users, Download, FileSpreadsheet, BarChart3, LayoutGrid,
    Scale, TrendingUp, AlertTriangle, Info, RefreshCw, ChevronLeft,
    ChevronDown, ChevronUp
} from 'lucide-react';
import * as XLSX from 'xlsx';

import { getInstanceResults, getTemplateResults, getRespondentResults, getOrganizationResults } from '../services/results';
import {
    getOrganization,
    getTemplate,
    getSurvey,
    getSurveys,
    downloadRespondentReport,
    downloadGroupReport,
    downloadOrganizationReport
} from '../services/api';
import {
    toEmployees,
    extractCategories,
    getTeamStats,
    analyzeIndividual,
    getCultureType,
} from '../services/analytics';
import { getHebrewName } from '../constants/muscles';
import type { InstanceResults, TemplateResults, RespondentResult } from '../types/results';
import type { Organization, SurveyQuestion, ScaleOption } from '../types';

import {
    RadarView,
    ComparisonBarView,
    InsightsPanel,
    HeatmapView,
    DistributionView,
    MatrixView,
    ScatterView,
    TeamImbalanceView,
    PercentileView,
    InstanceAverageTable,
    DemographicComparisonView,
} from '../components/analytics';
import type { DemographicDimension } from '../constants/demographics';
import { DEMOGRAPHIC_LABELS } from '../constants/demographics';
import { filterSegmentsByPrivacy, getSegmentLabel, getSegmentColor } from '../services/demographics';

type ViewMode = 'individual' | 'group';
type GroupGraphMode = 'distribution' | 'matrix' | 'scatter' | 'imbalance';

interface AnalyticsDashboardPageProps {
    instanceId?: string;
    templateId?: number;
    organizationId?: string;
    respondentId?: string;
    onNavigate?: (params: { instanceId?: string, organizationId?: string, respondentId?: string, templateId?: number }) => void;
    onShowToast?: (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => void;
}


export default function AnalyticsDashboardPage({ instanceId, templateId, organizationId, respondentId, onNavigate, onShowToast }: AnalyticsDashboardPageProps) {
    // Data state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [instanceData, setInstanceData] = useState<InstanceResults | null>(null);
    const [templateData, setTemplateData] = useState<TemplateResults | null>(null);
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
    const [answerScale, setAnswerScale] = useState<Record<string, ScaleOption[]>>({});
    const [orgInstances, setOrgInstances] = useState<any[]>([]);
    const [respondentData, setRespondentData] = useState<RespondentResult | null>(null);

    // UI state
    const [view, setView] = useState<ViewMode>('group');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [groupGraphMode, setGroupGraphMode] = useState<GroupGraphMode>('distribution');
    const [scatterAxes, setScatterAxes] = useState<[string, string]>(['Initiative', 'Systematic']);
    const [tableMode, setTableMode] = useState<'group' | 'global'>('group');


    const [demographicDimension, setDemographicDimension] = useState<DemographicDimension | null>(null);
    const [secondaryDimension, setSecondaryDimension] = useState<DemographicDimension | null>(null);

    const [showAllAnswers, setShowAllAnswers] = useState(false);
    const [questionCategoryFilter, setQuestionCategoryFilter] = useState<string>('all');

    // Fetch data
    useEffect(() => {
        async function loadData() {
            setLoading(true);
            setError(null);

            if (!respondentId) {
                setRespondentData(null);
            }

            try {
                if (respondentId) {
                    const respResult = await getRespondentResults(respondentId);
                    setRespondentData(respResult);

                    const effectiveInstanceId = respResult.instance_id || respResult.survey_instance_id || instanceId;
                    const effectiveTemplateId = respResult.template_id || templateId;
                    const effectiveOrgId = respResult.organization_id || organizationId;

                    const templatePromise = effectiveTemplateId ? getTemplate(effectiveTemplateId) : Promise.resolve(null);
                    const orgPromise = effectiveOrgId ? getOrganization(effectiveOrgId) : Promise.resolve(null);
                    const instancePromise = effectiveInstanceId ? getInstanceResults(effectiveInstanceId) : Promise.resolve(null);

                    const [template, org, instanceRes] = await Promise.all([templatePromise, orgPromise, instancePromise]);

                    if (instanceRes) {
                        setInstanceData(instanceRes);
                        if (instanceRes.answer_scale) setAnswerScale(instanceRes.answer_scale);
                    }

                    if (org) setOrganization(org);
                    if (template) {
                        if (template.questions) setQuestions(template.questions);
                        if (template.answer_scale && !instanceRes?.answer_scale) setAnswerScale(template.answer_scale);
                    }

                    // Set selected employee to ensure it shows up in individual view
                    setSelectedEmployeeId(respondentId);
                    setView('individual');
                } else if (instanceId) {
                    // Get instance results first - it contains template_id and organization_id
                    const res = await getInstanceResults(instanceId);
                    setInstanceData(res);
                    if (res.answer_scale) setAnswerScale(res.answer_scale);

                    const [template, org] = await Promise.all([
                        res.template_id ? getTemplate(res.template_id) : Promise.resolve(null),
                        res.organization_id ? getOrganization(res.organization_id) : Promise.resolve(null)
                    ]);
                    if (org) setOrganization(org);
                    if (template) {
                        if (template.questions) setQuestions(template.questions);
                        if (template.answer_scale && !res.answer_scale) setAnswerScale(template.answer_scale);
                    }
                } else if (templateId) {
                    const [res, template] = await Promise.all([
                        getTemplateResults(templateId),
                        getTemplate(templateId)
                    ]);
                    setTemplateData(res);
                    if (res.answer_scale) setAnswerScale(res.answer_scale);
                    if (template.questions) setQuestions(template.questions);
                    if (template.answer_scale && !res.answer_scale) setAnswerScale(template.answer_scale);
                } else if (organizationId) {
                    const res = await getOrganizationResults(organizationId);
                    // Use organization data from results or fetch separately
                    const org = res.is_global !== undefined ? {
                        id: res.organization_id,
                        name: res.organization_name,
                        is_global: res.is_global
                    } as Organization : await getOrganization(organizationId);

                    setOrganization(org);
                    if (res.answer_scale) setAnswerScale(res.answer_scale);

                    const orgName = res.organization_name || org.name || 'ארגון';

                    if (res.instances && res.instances.length > 0) {
                        setOrgInstances(res.instances);
                        const instanceAsRespondents = res.instances.map((inst: any) => ({
                            id: inst.id,
                            name: inst.name,
                            email: '',
                            status: inst.isActive ? 'active' : 'inactive',
                            category_stats: inst.averages,
                        }));

                        setInstanceData({
                            instance_id: organizationId,
                            survey_name: `דוח ארגוני: ${orgName}`,
                            created_at: '',
                            respondents: instanceAsRespondents,
                            ageAverages: res.ageAverages,
                            genderAverages: res.genderAverages,
                            demographicAverages: res.demographicAverages,
                        } as any);
                    } else {
                        // Legacy fallback - use respondents if available
                        setInstanceData({
                            instance_id: organizationId,
                            survey_name: `דוח ארגוני: ${orgName}`,
                            created_at: '',
                            respondents: res.respondents || [],
                            ageAverages: res.ageAverages,
                            genderAverages: res.genderAverages,
                            demographicAverages: res.demographicAverages,
                        } as any);
                    }

                    const surveys = await getSurveys();
                    const orgSurveys = surveys.filter((s: any) => String(s.organization_id) === String(organizationId));
                    const templateIds = [...new Set(orgSurveys.map((s: any) => s.template_id).filter(id => id !== undefined))];

                    if (templateIds.length > 0) {
                        try {
                            const templatePromises = templateIds.map(id => getTemplate(id as number));
                            const settlements = await Promise.allSettled(templatePromises);

                            const templatesData = settlements
                                .filter((s): s is PromiseFulfilledResult<any> => s.status === 'fulfilled')
                                .map(s => s.value);

                            const allQuestions: SurveyQuestion[] = [];
                            const combinedScale: Record<string, ScaleOption[]> = {};
                            const seenQuestionIds = new Set<string | number>();

                            templatesData.forEach(template => {
                                if (template.questions) {
                                    template.questions.forEach((q: any) => {
                                        if (!seenQuestionIds.has(q.id)) {
                                            allQuestions.push(q);
                                            seenQuestionIds.add(q.id);
                                        }
                                    });
                                }
                                if (template.answer_scale) {
                                    Object.entries(template.answer_scale).forEach(([key, opt]) => {
                                        if (!combinedScale[key]) {
                                            combinedScale[key] = opt as ScaleOption[];
                                        }
                                    });
                                }
                            });

                            setQuestions(allQuestions);
                            setAnswerScale(combinedScale);
                        } catch (e) {
                            console.error("Critical error in template merging", e);
                        }
                    } else if (res.respondents && res.respondents.length > 0) {
                        console.warn("No linked templates found via surveys, trying fallback via respondents...");
                        const respondentWithInstance = res.respondents.find(r => r.instance_id || r.survey_instance_id);
                        if (respondentWithInstance) {
                            try {
                                const sId = respondentWithInstance.instance_id || respondentWithInstance.survey_instance_id!;
                                const survey = await getSurvey(sId);
                                const template = await getTemplate(survey.template_id);
                                if (template.questions) setQuestions(template.questions);
                                if (template.answer_scale) setAnswerScale(template.answer_scale);
                            } catch (e) {
                                console.warn("Failed to fetch fallback template", e);
                            }
                        }
                    }
                } else {
                    setError('נדרש מזהה סקר, תבנית או ארגון');
                }
            } catch (err) {
                console.error(err);
                setError(err instanceof Error ? err.message : 'שגיאה בטעינת הנתונים');
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [instanceId, templateId, organizationId, respondentId]);

    // Derived data
    const employees = useMemo(() => {
        const respondents = instanceData?.respondents || templateData?.respondents || [];
        let result = toEmployees(respondents, questions, answerScale);

        // For global instances with no visible respondents but with averages, create a synthetic entry
        if (result.length === 0 && instanceData?.is_global && instanceData?.instanceAverages) {
            result = [{
                id: instanceData.instance_id || 'global-avg',
                name: instanceData.instance_name || instanceData.survey_name || 'ממוצע קבוצה',
                email: '',
                scores: instanceData.instanceAverages,
            }];
        }

        // If respondentData is loaded for a specific respondent not in the list
        // (e.g., from a global org where individual respondents aren't returned),
        // inject them so selectedEmployee ID matches and percentiles display correctly.
        // Replace synthetic global-avg entry to keep the dropdown clean.
        if (respondentData?.id && respondentData.category_stats) {
            const exists = result.some(e => e.id === respondentData.id);
            if (!exists) {
                result = result.filter(e => e.id !== (instanceData?.instance_id) && e.id !== 'global-avg');
                result.unshift({
                    id: respondentData.id,
                    name: respondentData.name || '',
                    email: respondentData.email || '',
                    scores: respondentData.category_stats,
                });
            }
        }

        return result;
    }, [instanceData, templateData, questions, answerScale, respondentData]);

    const categories = useMemo(() => {
        const respondents = instanceData?.respondents || templateData?.respondents || [];
        let result = employees.length > 0 ? extractCategories(respondents, questions) : [];

        if (result.length === 0 && instanceData?.instanceAverages) {
            result = Object.keys(instanceData.instanceAverages);
        }

        return result;
    }, [employees, instanceData, templateData, questions]);

    const teamStats = useMemo(() => {
        const respondents = instanceData?.respondents || templateData?.respondents || [];
        const stats = getTeamStats(respondents, questions, answerScale);

        if (instanceData?.is_global && instanceData?.instanceAverages && Object.keys(stats.averages).length === 0) {
            const avgEntries = Object.entries(instanceData.instanceAverages);
            const sorted = avgEntries.sort((a, b) => b[1] - a[1]);
            return {
                averages: instanceData.instanceAverages,
                superpower: sorted[0] || ['', 0],
                blindspot: sorted[sorted.length - 1] || ['', 0],
                respondentCount: instanceData.respondentCount || 1,
                completedCount: instanceData.respondentCount || 1,
            };
        }

        return stats;
    }, [instanceData, templateData, questions, answerScale]);

    const selectedEmployee = useMemo(() => {
        if (!selectedEmployeeId) return employees[0] || null;
        return employees.find(e => e.id === selectedEmployeeId) || employees[0] || null;
    }, [selectedEmployeeId, employees]);

    const insights = useMemo(() => {
        if (!selectedEmployee) return [];
        return analyzeIndividual(selectedEmployee, {
            ageGroupAverages: respondentData?.ageGroupAverages,
            genderGroupAverages: respondentData?.genderGroupAverages,
            ageBucket: respondentData?.age_bucket,
            genderNormalized: respondentData?.gender_normalized,
        });
    }, [selectedEmployee, respondentData]);

    const avgPercentiles = useMemo(() => {
        if (!selectedEmployee || categories.length === 0) return { global: 0, org: 0 };

        let totalGlobal = 0;
        let totalOrg = 0;

        categories.forEach(cat => {
            // Global
            const exactGlobalPercentiles = respondentData?.id === selectedEmployee.id ? respondentData.globalPercentiles : null;
            if (exactGlobalPercentiles && exactGlobalPercentiles[cat] !== undefined) {
                totalGlobal += exactGlobalPercentiles[cat];
            }

            // Org / Instance
            // Use pre-calculated instance percentiles if available (prefer respondentData)
            const exactInstancePercentiles = (respondentData?.id === selectedEmployee.id ? respondentData.instancePercentiles : null) || selectedEmployee.instancePercentiles;
            if (exactInstancePercentiles && exactInstancePercentiles[cat] !== undefined) {
                totalOrg += exactInstancePercentiles[cat];
            }
        });

        return {
            global: Math.round(totalGlobal / categories.length),
            org: Math.round(totalOrg / categories.length)
        };
    }, [selectedEmployee, categories, respondentData]);

    const selectedRespondent = useMemo(() => {
        if (respondentData) {
            return respondentData;
        }
        const respondents = instanceData?.respondents || templateData?.respondents || [];
        return respondents.find(r => r.id === selectedEmployee?.id);
    }, [selectedEmployee, instanceData, templateData, respondentData]);

    const isOrgView = useMemo(() => !!organizationId && !instanceId, [organizationId, instanceId]);
    const isGlobal = useMemo(() => organization?.is_global === true, [organization]);
    const hideTeamComparison = isGlobal || teamStats.respondentCount < 8;

    const demographicDisplaySegments = useMemo(() => {
        if (!demographicDimension) return null;

        const rawSegments = demographicDimension === 'age'
            ? instanceData?.ageAverages
            : instanceData?.genderAverages;

        if (!rawSegments || rawSegments.length === 0) return null;

        const filtered = filterSegmentsByPrivacy(rawSegments);

        return filtered.map(s => ({
            key: s.segment,
            labelHe: getSegmentLabel(s.segment, demographicDimension),
            count: s.count,
            averages: s.averages,
            color: getSegmentColor(s.segment),
        }));
    }, [instanceData, demographicDimension, employees]);

    const secondaryDisplaySegments = useMemo(() => {
        if (!secondaryDimension) return null;

        const rawSegments = secondaryDimension === 'age'
            ? instanceData?.ageAverages
            : instanceData?.genderAverages;

        if (!rawSegments || rawSegments.length === 0) return null;

        return filterSegmentsByPrivacy(rawSegments).map(s => ({
            key: s.segment,
            labelHe: getSegmentLabel(s.segment, secondaryDimension),
            count: s.count,
            averages: s.averages,
            color: getSegmentColor(s.segment),
        }));
    }, [instanceData, secondaryDimension, employees]);

    const effectiveInstanceId = useMemo(() => {
        const id = instanceId || instanceData?.instance_id || respondentData?.instance_id || respondentData?.survey_instance_id;
        return (id && id !== 'undefined') ? id : undefined;
    }, [instanceId, instanceData, respondentData]);

    const surveyName = useMemo(() => {
        return instanceData?.survey_name || respondentData?.instance_name || templateData?.template_name || 'ניתוח חוסן';
    }, [instanceData, respondentData, templateData]);

    const entityLabel = isOrgView ? 'ארגון' : 'קבוצה';
    const entityLabelPossessive = isOrgView ? 'הארגון' : 'הקבוצה';
    const entityReportLabel = isOrgView ? 'ארגוני' : 'קבוצתי';

    // Force correct view based on context
    useEffect(() => {
        if (!loading) {
            if (respondentId) {
                setView('individual');
            } else {
                setView('group');
            }
        }
    }, [loading, respondentId]);

    // Set table mode for global comparison if it's a global organization or not enough respondents
    useEffect(() => {
        if (!loading && hideTeamComparison) {
            setTableMode('global');
        }
    }, [loading, hideTeamComparison]);

    // Handle deep linking to specific respondent
    useEffect(() => {
        if (!loading && respondentId && employees.length > 0) {
            const exists = employees.find(e => e.id === respondentId);
            if (exists) {
                setSelectedEmployeeId(respondentId);
                if (!organization?.is_global) {
                    setView('individual');
                }
            }
        }
    }, [loading, respondentId, employees, organization]);

    const handleEmployeeSelect = async (id: string) => {
        if (onNavigate) {
            onNavigate({
                instanceId: effectiveInstanceId,
                organizationId: organization?.id || organizationId,
                respondentId: id
            });
            return;
        }

        setSelectedEmployeeId(id);
        setView('individual');

        try {
            const respResult = await getRespondentResults(id);
            setRespondentData(respResult);
        } catch {
            // Silently fail - view will work without answers
        }
    };

    const handleMatrixCellClick = (pair: [string, string]) => {
        setScatterAxes(pair);
        setGroupGraphMode('scatter');
    };

    const handleRefresh = async () => {
        if (instanceId) {
            const res = await getInstanceResults(instanceId);
            setInstanceData(res);
            if (res.template_id) {
                const template = await getTemplate(res.template_id);
                if (template.questions) setQuestions(template.questions);
                if (template.answer_scale) setAnswerScale(template.answer_scale);
            }
        } else if (templateId) {
            const [res, template] = await Promise.all([
                getTemplateResults(templateId),
                getTemplate(templateId)
            ]);
            setTemplateData(res);
            if (template.questions) setQuestions(template.questions);
            if (template.answer_scale) setAnswerScale(template.answer_scale);
        } else if (organizationId) {
            // organization reload logic - use getOrganizationResults to include demographic data
            const res = await getOrganizationResults(organizationId);
            const org = res.is_global !== undefined ? {
                id: res.organization_id,
                name: res.organization_name,
                is_global: res.is_global
            } as Organization : await getOrganization(organizationId);

            setOrganization(org);
            const orgName = res.organization_name || org.name || 'ארגון';

            if (res.instances && res.instances.length > 0) {
                setOrgInstances(res.instances);
                const instanceAsRespondents = res.instances.map((inst: any) => ({
                    id: inst.id,
                    name: inst.name,
                    email: '',
                    status: inst.isActive ? 'active' : 'inactive',
                    category_stats: inst.averages,
                }));
                setInstanceData({
                    instance_id: organizationId,
                    survey_name: `דוח ארגוני: ${orgName}`,
                    created_at: '',
                    respondents: instanceAsRespondents,
                    ageAverages: res.ageAverages,
                    genderAverages: res.genderAverages,
                    demographicAverages: res.demographicAverages,
                } as any);
            } else {
                setInstanceData({
                    instance_id: organizationId,
                    survey_name: `דוח ארגוני: ${orgName}`,
                    created_at: '',
                    respondents: res.respondents || [],
                    ageAverages: res.ageAverages,
                    genderAverages: res.genderAverages,
                    demographicAverages: res.demographicAverages,
                } as any);
            }
        }
    };

    const handleExportPDF = async () => {
        if (!selectedEmployee) return;

        // Immediate feedback using global toast
        onShowToast?.('info', 'מוריד דוח', 'הדוח מופק ברקע...');

        try {
            const respondentId = respondentData?.id || selectedEmployee.id;
            // Run in background
            downloadRespondentReport(respondentId).then(() => {
                onShowToast?.('success', 'הורדה הושלמה', 'הדוח ירד בהצלחה');
            }).catch(err => {
                console.error('PDF Download failed:', err);
                onShowToast?.('error', 'שגיאה', 'אירעה שגיאה בהורדת ה-PDF');
            });
        } catch (err) {
            console.error('PDF Download trigger failed:', err);
        }
    };

    const handleGroupExportPDF = async () => {
        // Immediate feedback using global toast
        onShowToast?.('info', 'מוריד דוח', 'הדוח מופק ברקע...');

        try {
            if (isOrgView && organizationId) {
                downloadOrganizationReport(organizationId).then(() => {
                    onShowToast?.('success', 'הורדה הושלמה', 'הדוח הארגוני ירד בהצלחה');
                }).catch(err => {
                    console.error('Organization PDF Download failed:', err);
                    onShowToast?.('error', 'שגיאה', 'אירעה שגיאה בהורדת הדוח הארגוני');
                });
            } else if (instanceData?.instance_id) {
                downloadGroupReport(instanceData.instance_id).then(() => {
                    onShowToast?.('success', 'הורדה הושלמה', 'דוח הקבוצה ירד בהצלחה');
                }).catch(err => {
                    console.error('Group PDF Download failed:', err);
                    onShowToast?.('error', 'שגיאה', 'אירעה שגיאה בהורדת דוח הקבוצה');
                });
            } else if (instanceId) {
                downloadGroupReport(instanceId).then(() => {
                    onShowToast?.('success', 'הורדה הושלמה', 'דוח הקבוצה ירד בהצלחה');
                }).catch(err => {
                    console.error('Group PDF Download failed:', err);
                    onShowToast?.('error', 'שגיאה', 'אירעה שגיאה בהורדת דוח הקבוצה');
                });
            }
        } catch (err) {
            console.error('Group PDF Download trigger failed:', err);
        }
    };

    const handleExportExcel = () => {
        try {
            const exportData = employees.map(emp => {
                const row: Record<string, any> = { 'שם המשתתף': emp.name };

                categories.forEach(cat => {
                    row[getHebrewName(cat)] = emp.scores[cat]?.toFixed(2) ?? '';
                });

                const avg = Object.values(emp.scores).reduce((a, b) => a + b, 0) / Object.keys(emp.scores).length;
                row['ציון ממוצע'] = avg.toFixed(2);

                return row;
            });

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wscols = [
                { wch: 20 },
                ...categories.map(() => ({ wch: 15 })),
                { wch: 12 },
            ];
            ws['!cols'] = wscols;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'נתוני ארגון');

            const filename = instanceData?.survey_name || templateData?.template_name || 'Team_Data';
            XLSX.writeFile(wb, `${filename}.xlsx`);
        } catch (err) {
            console.error('Excel Export failed:', err);
            alert('אירעה שגיאה בייצוא לאקסל');
        }
    };

    if (loading) {
        return (
            <div className="analytics-loading">
                <RefreshCw className="analytics-loading-icon" />
                <p>טוען נתונים...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="analytics-error">
                <AlertTriangle className="analytics-error-icon" />
                <p>{error}</p>
                <button onClick={handleRefresh} className="analytics-retry-btn">
                    נסה שוב
                </button>
            </div>
        );
    }

    if (employees.length === 0) {
        return (
            <div className="analytics-empty">
                <Info className="analytics-empty-icon" />
                <h3>אין נתונים להצגה</h3>
                <p>לא נמצאו משתתפים שהשלימו את הסקר. ייתכן שהמשתתפים טרם סיימו את המענה.</p>
                <button
                    onClick={() => window.location.reload()}
                    className="btn btn-secondary mt-4"
                    style={{ marginTop: '16px' }}
                >
                    רענן דף
                </button>
            </div>
        );
    }

    return (
        <div className="analytics-dashboard" dir="rtl">
            {/* Header */}
            <div className="analytics-header-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: 'var(--neutral-500)', fontSize: '14px' }}>
                    <span
                        style={{ cursor: onNavigate ? 'pointer' : 'default' }}
                        onClick={() => onNavigate?.({})}
                    >
                        ארגונים
                    </span>
                    {organization && !surveyName.includes(organization.name) && (
                        <>
                            <ChevronLeft size={14} />
                            <span
                                style={{ cursor: onNavigate ? 'pointer' : 'default' }}
                                onClick={() => onNavigate?.({ organizationId: organization.id })}
                            >
                                {organization.name}
                            </span>
                        </>
                    )}
                    {effectiveInstanceId && (
                        <>
                            <ChevronLeft size={14} />
                            <span
                                style={{
                                    cursor: onNavigate ? 'pointer' : 'default',
                                    fontWeight: 500,
                                    color: 'var(--primary-600)',
                                    textDecoration: view === 'individual' ? 'underline' : 'none',
                                    textUnderlineOffset: '4px',
                                    userSelect: 'none'
                                }}
                                onClick={(e) => {
                                    if (!onNavigate) return;

                                    e.preventDefault();
                                    e.stopPropagation();

                                    // Use current values directly from props/state, not closure
                                    const currentInstanceId = instanceId || instanceData?.instance_id || respondentData?.instance_id || respondentData?.survey_instance_id;
                                    if (!currentInstanceId || currentInstanceId === 'undefined') return;

                                    onNavigate({
                                        instanceId: currentInstanceId,
                                        organizationId: organization?.id || organizationId,
                                        // Remove templateId to prevent sticking to "template mode"
                                        // templateId: instanceData?.template_id || respondentData?.template_id || templateId,
                                        respondentId: undefined
                                    });
                                }}
                            >
                                {surveyName}
                            </span>
                        </>
                    )}
                    {view === 'individual' && selectedEmployee && (
                        <>
                            <ChevronLeft size={14} />
                            <span style={{ fontWeight: 500, color: 'var(--primary-600)' }}>{selectedEmployee.name}</span>
                        </>
                    )}
                </div>

                <div className="analytics-header">
                    <div>
                        <h1 className="analytics-title">
                            {view === 'individual' && selectedEmployee ? selectedEmployee.name : surveyName}
                        </h1>
                        <p className="analytics-subtitle">
                            {view === 'individual' && selectedEmployee
                                ? surveyName
                                : `${teamStats.completedCount} משתתפים השלימו מתוך ${teamStats.respondentCount}`
                            }
                        </p>
                    </div>

                    <div className="analytics-header-actions">
                        <div className="analytics-view-toggle">
                            {!!respondentId && (
                                <button
                                    className={`analytics-view-btn ${view === 'individual' ? 'active' : ''}`}
                                    onClick={() => setView('individual')}
                                >
                                    <User size={16} /> פרופיל אישי
                                </button>
                            )}

                            <button
                                className={`analytics-view-btn ${view === 'group' ? 'active' : ''}`}
                                onClick={() => {
                                    if (isGlobal && !!respondentId && onNavigate) {
                                        onNavigate({ organizationId: organization?.id || organizationId });
                                    } else if (!!respondentId && onNavigate) {
                                        // If we are in respondent view, navigate back to group view (instance root)
                                        // instead of just changing local state, to clean up URL
                                        onNavigate({
                                            instanceId: effectiveInstanceId,
                                            organizationId: organization?.id || organizationId,
                                            respondentId: undefined
                                        });
                                    } else {
                                        setView('group');
                                    }
                                }}
                            >
                                <Users size={16} /> {isGlobal ? (isOrgView ? 'תצוגה גלובלית' : 'לוח בקרה ארגוני') : (isOrgView ? 'תצוגה ארגונית' : 'תצוגת קבוצה')}
                            </button>
                        </div>

                        <button onClick={handleRefresh} className="analytics-icon-btn" title="רענן נתונים">
                            <RefreshCw size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Individual View */}
            {
                view === 'individual' && selectedEmployee && (
                    <div className="analytics-individual">
                        {/* Employee Selector & Actions */}
                        <div className="analytics-individual-header">
                            <div className="analytics-employee-select">
                                <label>בחר משתתף:</label>
                                <select
                                    value={selectedEmployee.id}
                                    disabled={isGlobal}
                                    onChange={(e) => {
                                        // Use navigation to change employee so URL updates
                                        if (onNavigate) {
                                            onNavigate({
                                                instanceId: effectiveInstanceId,
                                                organizationId: organization?.id || organizationId,
                                                respondentId: e.target.value
                                            });
                                        } else {
                                            handleEmployeeSelect(e.target.value);
                                        }
                                    }}
                                >
                                    {employees.map(e => (
                                        <option key={e.id} value={e.id}>{e.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="analytics-actions">
                                <div className="analytics-avg-badge">
                                    ממוצע אישי: <strong>{(Object.values(selectedEmployee.scores).reduce((a, b) => a + b, 0) / categories.length).toFixed(2)}</strong>
                                </div>
                                <div className="analytics-avg-badge" style={{ backgroundColor: 'var(--warning-50)', color: 'var(--warning-700)', borderColor: 'var(--warning-100)' }}>
                                    אחוזון גלובלי: <strong>{avgPercentiles.global}%</strong>
                                </div>
                                {(employees.length > 1) && (
                                    <div className="analytics-avg-badge" style={{ backgroundColor: 'var(--success-50)', color: 'var(--success-700)', borderColor: 'var(--success-100)' }}>
                                        אחוזון {entityLabel}: <strong>{avgPercentiles.org}%</strong>
                                    </div>
                                )}
                                {respondentData?.age_bucket && respondentData.age_bucket !== 'unknown' && respondentData.ageGroupCount && respondentData.ageGroupCount >= 3 && (
                                    <span className="analytics-avg-badge" style={{ backgroundColor: '#5B8C5A15', color: '#5B8C5A', borderColor: '#5B8C5A30' }}>
                                        {DEMOGRAPHIC_LABELS.age}: {respondentData.age_bucket} ({respondentData.ageGroupCount} משתתפים)
                                    </span>
                                )}
                                {respondentData?.gender_normalized && respondentData.gender_normalized !== 'unknown' && respondentData.gender_normalized !== 'other' && respondentData.genderGroupCount && respondentData.genderGroupCount >= 3 && (
                                    <span className="analytics-avg-badge" style={{ backgroundColor: '#8B5E8315', color: '#8B5E83', borderColor: '#8B5E8330' }}>
                                        {DEMOGRAPHIC_LABELS.gender}: {getSegmentLabel(respondentData.gender_normalized, 'gender')} ({respondentData.genderGroupCount} משתתפים)
                                    </span>
                                )}
                                <button
                                    onClick={handleExportPDF}
                                    className="analytics-export-btn"
                                >
                                    <Download size={16} />
                                    ייצוא {isGlobal ? 'דוח ארגוני' : 'דוח אישי'}
                                </button>
                            </div>
                        </div>

                        {/* Charts Grid */}
                        <div className="analytics-individual-grid">
                            {/* Left Column: Charts */}
                            <div className="analytics-charts-col">
                                {/* Radar Chart */}
                                <div className="analytics-card">
                                    <h3 className="analytics-card-title">מבנה הפרופיל (Spider)</h3>
                                    <div className="analytics-radar-container">
                                        <RadarView
                                            employee={selectedEmployee}
                                            groupAvg={respondentData?.instanceAverages || instanceData?.instanceAverages || teamStats.averages}
                                            globalAvg={respondentData?.globalAverages || instanceData?.globalAverages}
                                            hideTeam={hideTeamComparison}
                                            ageGroupAvg={respondentData?.ageGroupAverages}
                                            genderGroupAvg={respondentData?.genderGroupAverages}
                                        />
                                    </div>
                                </div>

                                {/* Bar Chart */}
                                <div className="analytics-card analytics-bar-card">
                                    <h3 className="analytics-card-title">
                                        <BarChart3 size={20} className="text-blue-500" />
                                        השוואה לנורמות
                                    </h3>
                                    <div className="analytics-bar-container">
                                        <ComparisonBarView
                                            employee={selectedEmployee}
                                            groupAvg={respondentData?.instanceAverages || instanceData?.instanceAverages || teamStats.averages}
                                            globalAvg={respondentData?.globalAverages || instanceData?.globalAverages}
                                            hideTeam={hideTeamComparison}
                                            ageGroupAvg={respondentData?.ageGroupAverages}
                                            genderGroupAvg={respondentData?.genderGroupAverages}
                                        />
                                    </div>
                                </div>

                                {/* Percentile Chart */}
                                <div className="analytics-card">
                                    <h3 className="analytics-card-title">מיקום יחסי (אחוזונים)</h3>
                                    <div className="analytics-percentile-container">
                                        <PercentileView
                                            employee={selectedEmployee}
                                            globalPercentiles={respondentData?.id === selectedEmployee.id ? respondentData.globalPercentiles : undefined}
                                            instancePercentiles={respondentData?.id === selectedEmployee.id ? respondentData.instancePercentiles : undefined}
                                            isOrgView={isOrgView}
                                            hideTeam={hideTeamComparison}
                                            ageGroupPercentiles={respondentData?.ageGroupPercentiles}
                                            genderGroupPercentiles={respondentData?.genderGroupPercentiles}
                                        />
                                    </div>
                                </div>

                                {/* Data Table */}
                                <div className="analytics-card">
                                    <div className="analytics-table-header">
                                        <h3 className="analytics-card-title">
                                            <LayoutGrid size={20} className="text-indigo-500" />
                                            פירוט נתונים מלא
                                        </h3>
                                        <div className="analytics-table-toggle">
                                            {!hideTeamComparison && (
                                                <button
                                                    className={tableMode === 'group' ? 'active' : ''}
                                                    onClick={() => setTableMode('group')}
                                                >
                                                    מול {entityLabelPossessive}
                                                </button>
                                            )}
                                            <button
                                                className={tableMode === 'global' ? 'active' : ''}
                                                onClick={() => setTableMode('global')}
                                            >
                                                מול ממוצע גלובלי
                                            </button>
                                        </div>
                                    </div>
                                    <div className="analytics-table-container">
                                        <table className="analytics-table">
                                            <thead>
                                                <tr>
                                                    <th>שריר חוסן</th>
                                                    <th>ציון אישי</th>
                                                    <th>{tableMode === 'group' ? `ממוצע ${entityLabel}` : 'ממוצע גלובלי'}</th>
                                                    <th>{tableMode === 'group' ? `פער מ${entityLabelPossessive}` : 'פער מהממוצע'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categories.map(cat => {
                                                    const personal = selectedEmployee.scores[cat] ?? 0;
                                                    // Use pre-calculated instanceAverages from SQL-First API when available
                                                    // Fall back to teamStats.averages for legacy compatibility
                                                    const comparisonValue = tableMode === 'group'
                                                        ? (instanceData?.instanceAverages?.[cat] ?? teamStats.averages[cat] ?? 0)
                                                        : (instanceData?.globalAverages?.[cat] ?? 6.0);
                                                    const gap = personal - comparisonValue;
                                                    return (
                                                        <tr key={cat}>
                                                            <td className="text-right font-medium">{getHebrewName(cat)}</td>
                                                            <td className="font-bold">{personal.toFixed(2)}</td>
                                                            <td className="text-gray-500">{comparisonValue.toFixed(2)}</td>
                                                            <td className={`font-bold ${gap > 0 ? 'text-green-600' : 'text-red-500'}`} dir="ltr">
                                                                {gap > 0 ? '+' : ''}{gap.toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Raw Answers Section */}
                                <div className="analytics-card" style={{ marginTop: '24px' }}>
                                    <div
                                        className="analytics-collapsible-header"
                                        onClick={() => setShowAllAnswers(!showAllAnswers)}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                                    >
                                        <h3 className="analytics-card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <Scale size={20} style={{ color: 'var(--primary-500)' }} />
                                            תשובות גולמיות לשאלות הסקר
                                        </h3>
                                        <button className="btn btn-icon btn-ghost sm">
                                            {showAllAnswers ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                        </button>
                                    </div>

                                    {showAllAnswers && (
                                        <div className="analytics-answers-list" style={{ marginTop: '20px', borderTop: '1px solid var(--neutral-100)', paddingTop: '10px' }}>
                                            {/* Filter UI */}
                                            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--neutral-600)' }}>סנן לפי קטגוריה:</span>
                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                    <button
                                                        onClick={() => setQuestionCategoryFilter('all')}
                                                        className={`btn sm ${questionCategoryFilter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
                                                        style={{ fontSize: '12px', height: '28px', padding: '0 10px' }}
                                                    >
                                                        הכל
                                                    </button>
                                                    {categories.map(cat => (
                                                        <button
                                                            key={cat}
                                                            onClick={() => setQuestionCategoryFilter(cat)}
                                                            className={`btn sm ${questionCategoryFilter === cat ? 'btn-primary' : 'btn-ghost'}`}
                                                            style={{ fontSize: '12px', height: '28px', padding: '0 10px' }}
                                                        >
                                                            {getHebrewName(cat)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {questions.length === 0 ? (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--neutral-500)' }}>
                                                    לא נמצאה רשימת שאלות להצגה
                                                </div>
                                            ) : (
                                                questions
                                                    .slice()
                                                    .filter(q => questionCategoryFilter === 'all' || q.category === questionCategoryFilter)
                                                    .sort((a, b) => a.order_index - b.order_index)
                                                    .map(question => {
                                                        const answer = selectedRespondent?.answers?.find(a => String(a.question_id) === String(question.id));
                                                        return (
                                                            <div key={question.id} className="analytics-answer-item" style={{ padding: '16px 0', borderBottom: '1px solid var(--neutral-50)' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '4px' }}>
                                                                    <span style={{ fontWeight: 500, fontSize: '15px', color: 'var(--neutral-800)', flex: 1 }}>{question.text_he}</span>
                                                                    <div style={{
                                                                        minWidth: '36px',
                                                                        height: '36px',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: answer ? 'var(--primary-50)' : 'var(--neutral-100)',
                                                                        color: answer ? 'var(--primary-700)' : 'var(--neutral-400)',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontWeight: 700,
                                                                        fontSize: '16px'
                                                                    }}>
                                                                        {answer?.value || '-'}
                                                                    </div>
                                                                </div>
                                                                {question.category && (
                                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                        <span style={{ fontSize: '12px', color: 'var(--neutral-500)', backgroundColor: 'var(--neutral-50)', padding: '2px 8px', borderRadius: '4px' }}>
                                                                            {getHebrewName(question.category)}
                                                                        </span>
                                                                        {question.score_inversion && (
                                                                            <span style={{ fontSize: '11px', color: 'var(--warning-600)', fontStyle: 'italic' }}>
                                                                                * שאלת היפוך
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                            )}
                                            {questions.filter(q => questionCategoryFilter === 'all' || q.category === questionCategoryFilter).length === 0 && questions.length > 0 && (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--neutral-500)' }}>
                                                    אין שאלות בקטגוריה זו
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column: Insights */}
                            <div className="analytics-insights-col">
                                <InsightsPanel insights={insights} />
                            </div>
                        </div>

                    </div>
                )}

            {/* Group View */}
            {view === 'group' && (
                <div className="analytics-group">
                    {/* Summary Cards */}
                    <div className="analytics-summary-cards">
                        <div className="analytics-summary-card analytics-superpower">
                            <div className="analytics-summary-label">חוזקת העל של {entityLabelPossessive}</div>
                            <div className="analytics-summary-value">{getHebrewName(teamStats.superpower[0])}</div>
                            <div className="analytics-summary-score">ציון ממוצע: {teamStats.superpower[1].toFixed(2)}</div>
                        </div>
                        <div className="analytics-summary-card analytics-blindspot">
                            <div className="analytics-summary-label">ההזדמנות לצמיחה</div>
                            <div className="analytics-summary-value">{getHebrewName(teamStats.blindspot[0])}</div>
                            <div className="analytics-summary-score">ציון ממוצע: {teamStats.blindspot[1].toFixed(2)}</div>
                        </div>
                        <div className="analytics-summary-card analytics-culture">
                            <div className="analytics-summary-label">סוג התרבות</div>
                            <div className="analytics-summary-value">{getCultureType(teamStats.superpower[0])}</div>
                            <div className="analytics-summary-score">מבוסס על שריר דומיננטי</div>
                        </div>
                    </div>

                    {/* Export Actions */}
                    <div className="analytics-group-actions">
                        <button onClick={handleExportExcel} className="analytics-export-btn analytics-excel-btn">
                            <FileSpreadsheet size={16} />
                            ייצוא לאקסל
                        </button>
                        <button
                            onClick={handleGroupExportPDF}
                            className="analytics-export-btn"
                        >
                            <Download size={16} />
                            ייצוא דוח {entityReportLabel}
                        </button>
                    </div>

                    {/* Graph Area */}
                    <div className="analytics-group-grid">
                        <div className="analytics-group-main">
                            <div className="analytics-card">
                                <div className="analytics-graph-header">
                                    <div>
                                        <h3 className="analytics-card-title">ניתוח עומק ויזואלי</h3>
                                        <p className="analytics-card-subtitle">בחר סוג תצוגה לזיהוי דפוסים</p>
                                    </div>
                                    <div className="analytics-graph-tabs">
                                        <button
                                            className={groupGraphMode === 'matrix' ? 'active' : ''}
                                            onClick={() => setGroupGraphMode('matrix')}
                                        >
                                            <LayoutGrid size={14} /> מטריצה
                                        </button>
                                        <button
                                            className={groupGraphMode === 'scatter' ? 'active' : ''}
                                            onClick={() => setGroupGraphMode('scatter')}
                                        >
                                            <TrendingUp size={14} /> צמדים
                                        </button>
                                        <button
                                            className={groupGraphMode === 'imbalance' ? 'active' : ''}
                                            onClick={() => setGroupGraphMode('imbalance')}
                                        >
                                            <Scale size={14} /> איזון
                                        </button>
                                        <button
                                            className={groupGraphMode === 'distribution' ? 'active' : ''}
                                            onClick={() => setGroupGraphMode('distribution')}
                                        >
                                            <BarChart3 size={14} /> פיזור
                                        </button>
                                    </div>
                                    <div className="analytics-demographic-selector">
                                        <button
                                            className={`analytics-demographic-btn ${!demographicDimension ? 'active' : ''}`}
                                            onClick={() => { setDemographicDimension(null); setSecondaryDimension(null); }}
                                        >
                                            {DEMOGRAPHIC_LABELS.all}
                                        </button>
                                        <button
                                            className={`analytics-demographic-btn ${demographicDimension === 'age' ? 'active' : ''}`}
                                            onClick={() => { setDemographicDimension('age'); setSecondaryDimension(null); }}
                                        >
                                            {DEMOGRAPHIC_LABELS.viewByAge}
                                        </button>
                                        <button
                                            className={`analytics-demographic-btn ${demographicDimension === 'gender' ? 'active' : ''}`}
                                            onClick={() => { setDemographicDimension('gender'); setSecondaryDimension(null); }}
                                        >
                                            {DEMOGRAPHIC_LABELS.viewByGender}
                                        </button>
                                    </div>
                                </div>

                                <div className="analytics-graph-container">
                                    {groupGraphMode === 'distribution' && <DistributionView employees={employees} demographicDimension={demographicDimension} />}
                                    {groupGraphMode === 'matrix' && <MatrixView employees={employees} onPairSelect={handleMatrixCellClick} demographicDimension={demographicDimension} />}
                                    {groupGraphMode === 'scatter' && <ScatterView employees={employees} axes={scatterAxes} onAxesChange={setScatterAxes} demographicDimension={demographicDimension} />}
                                    {groupGraphMode === 'imbalance' && <TeamImbalanceView employees={employees} demographicDimension={demographicDimension} />}
                                </div>
                            </div>

                            {/* Demographic Comparison Chart */}
                            {demographicDimension && demographicDisplaySegments && demographicDisplaySegments.length >= 2 && (
                                <div className="analytics-card" style={{ marginTop: '24px' }}>
                                    <h3 className="analytics-card-title">
                                        השוואה דמוגרפית - {demographicDimension === 'age' ? DEMOGRAPHIC_LABELS.viewByAge : DEMOGRAPHIC_LABELS.viewByGender}
                                    </h3>
                                    <DemographicComparisonView
                                        segments={demographicDisplaySegments}
                                        secondarySegments={secondaryDisplaySegments}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Analysis Panel */}
                        <div className="analytics-group-side">
                            <div className="analytics-card">
                                <h3 className="analytics-card-title">ניתוח {entityReportLabel}</h3>
                                <p className="analytics-analysis-text">
                                    {entityLabelPossessive} הזה מצטיין ב<strong>{getHebrewName(teamStats.superpower[0])}</strong> אך מתקשה ב<strong>{getHebrewName(teamStats.blindspot[0])}</strong>.
                                    {teamStats.blindspot[0].includes('World') && ' ייתכן שישנה פסימיות לגבי שינויים בשוק.'}
                                    {teamStats.blindspot[0].includes('Focus') && ' ייתכן שחסר כיוון אחיד או מחויבות למטרות.'}
                                    {teamStats.blindspot[0].includes('Systematic') && ' ייתכן שיש אנרגיה גבוהה אך קושי בסגירת קצוות וביצוע.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Data List / Heatmap / Instance Average */}
                    {(isOrgView || !isGlobal) && (
                        <div className="analytics-card">
                            <h3 className="analytics-card-title">
                                {isOrgView ? 'פירוט קבוצות סקר' : `מפת חום ${entityReportLabel}`}
                            </h3>
                            {isOrgView ? (
                                <InstanceAverageTable
                                    respondents={instanceData?.respondents || []}
                                    employees={employees}
                                    allInstances={orgInstances}
                                    onNavigateToInstance={(id) => {
                                        if (onNavigate) {
                                            onNavigate({ instanceId: id, organizationId: organization?.id || organizationId });
                                        } else {
                                            const orgId = organization?.id || organizationId;
                                            let url = `?instance_id=${id}`;
                                            if (orgId) url += `&organization_id=${orgId}`;
                                            window.location.href = url;
                                        }
                                    }}
                                />
                            ) : (
                                <HeatmapView employees={employees} onSelectEmployee={handleEmployeeSelect} />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
